// ============================================================================
// LE BLOC « EXCLUSIONS DE CAMPAGNE » — « toute la boutique, SAUF … ».
//
// Arbre relevé de la maquette (`Sliderz Backoffice.dc.html`, livraison 2026-08-30) élément par
// élément : carte à en-tête (titre + sous-texte + pastille AMBRE du compte d'écartés), rangée de
// chips ambre à croix, bouton « Ajouter une exclusion » (bordure tiretée) qui ouvre un PANNEAU
// ANCRÉ (segmented des 6 dimensions, recherche, options à case à cocher, pied explicatif), lien
// « Tout retirer » au-delà de 8, bandeau ambre de réécriture, note grise finale.
//
// ⚠️ LA RECHERCHE DE VALEURS EST SERVEUR, jamais une liste en dur : 5 dimensions passent par les
// FACETTES du catalogue (`articleFacetSearch`, qui rend aussi le nombre de modèles porteurs), la
// 6ᵉ — l'article à l'unité — par la recherche d'articles (nom OU code, 2 caractères minimum).
// ⚠️ CE COMPOSANT NE COMPTE RIEN. Le nombre d'écartés vient du serveur (`comptes.exclusCampagne`),
// mesuré par la même expression SQL que le match du cœur ; l'estimer ici en dériverait une seconde
// vérité, qui finirait par contredire ce que la caisse applique (§23.0).
// ============================================================================
import { useEffect, useMemo, useRef, useState } from 'react';
import { confirm, Trans, useOverlayPanel } from '@orgazone/ui-web';
import { api, type ArticleFacet, type ExclusionSoldes } from '../api';
import { facetLabel } from '../vocabulary';
import { t } from '../i18n';

/** Les 6 dimensions offertes, dans l'ordre de la maquette. */
export const SX_DIMS: readonly ExclusionSoldes['dim'][] = ['sais', 'fam', 'sfam', 'marq', 'sect', 'code'];

/**
 * Le mot d'une dimension — celui du MAGASIN pour les cinq champs du catalogue, « Article » pour l'unité.
 * ⚠️ UNE FONCTION, PAS UNE TABLE DE MODULE : `fieldLabel` se lit au rendu. Une table remplie au
 *    chargement du fichier figerait « Saison » avant que le contexte magasin ne soit lu.
 */
export function sxDimLabel(dim: ExclusionSoldes['dim']): string {
  switch (dim) {
    case 'sais': case 'fam': case 'sfam': case 'marq': case 'sect': return facetLabel(dim);
    case 'code': return t('Article');
    // ⚠️ Une dimension que ce code ne connaît pas (serveur plus récent) s'affiche sous son nom
    //    technique — jamais du blanc, rien ne s'écrase en silence.
    default: return dim;
  }
}

/** Une valeur proposée par le panneau — `sub` = ce que le serveur en sait (nb de modèles, code…). */
interface OptionSx { value: string; label: string; sub: string }

const AMBRE_BG = 'var(--amber-bg)';
const AMBRE_FG = 'var(--amber-fg)';

export interface CarteExclusionsProps {
  /** Les exclusions COURANTES (état local de l'écran, initialisé par le serveur). */
  exclusions: ExclusionSoldes[];
  onChange: (next: ExclusionSoldes[]) => void;
  /** Modèles réellement écartés — MESURÉ par le serveur, jamais estimé ici. */
  campN: number;
  /** Campagne active dont l'état affiché diffère de ce qui est écrit aux caisses. */
  dirty: boolean;
  ecriture: boolean;
  onUpdate: () => void;
  /** Message RÉEL du dernier échec de lecture — la carte le dit et propose de réessayer. */
  failure: string | null;
  onRetry: () => void;
}

export function CarteExclusions(p: CarteExclusionsProps) {
  const [ouvert, setOuvert] = useState(false);
  /** Libellés retenus pour la dimension Article (le chip montre le nom, la base garde le code). */
  const [displayLabels, setLibelles] = useState<Record<string, string>>({});

  const retirer = (i: number) => p.onChange(p.exclusions.filter((_, x) => x !== i));

  async function toutRetirer() {
    const ok = await confirm({
      title: t('Retirer toutes les exclusions ?'),
      message: t('Toute la boutique éligible sera démarquée. Sur une campagne active, la réécriture les retire des caisses.'),
      confirmLabel: t('Tout retirer'), tone: 'danger',
    });
    if (ok) p.onChange([]);
  }

  return (
    <div style={{ background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', padding: '17px 18px', marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <span style={{ flex: 1, minWidth: 200 }}>
          <span style={{ display: 'block', fontSize: 'var(--t-strong)', fontWeight: 700, color: 'var(--ink-900)' }}>{t('Exclusions de campagne')}</span>
          <span style={{ display: 'block', fontSize: 'var(--t-dense)', color: 'var(--ink-500)', marginTop: 2 }}>
            <Trans k='Toute la boutique, <1>sauf</1> — collection en cours, marque à prix tenu, famille…' parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} /></span>
        </span>
        <span style={{
          height: 24, padding: '0 10px', borderRadius: 'var(--r-pill)', background: AMBRE_BG, color: AMBRE_FG,
          fontSize: 'var(--t-note)', fontWeight: 700, display: 'inline-flex', alignItems: 'center',
          fontVariantNumeric: 'tabular-nums', flexShrink: 0,
        }}>
          {t('{n} modèle écarté par les exclusions', { n: p.campN })}
        </span>
      </div>

      {/* L'échec de lecture est DIT avec son message réel : les comptes affichés datent d'avant. */}
      {p.failure !== null && (
        <div role="alert" style={{
          display: 'flex', alignItems: 'center', gap: 11, padding: '11px 14px', borderRadius: 'var(--r-sm)',
          background: 'var(--rose-bg)', border: '1px solid var(--rose)', marginBottom: 12, flexWrap: 'wrap',
        }}>
          <span style={{ flex: 1, minWidth: 220, fontSize: 'var(--t-body)', color: 'var(--rose-fg)', lineHeight: 1.5 }}>
            <Trans k='<1>Les soldes n’ont pas pu être relus</1> : {failure}. Les comptes et l’aperçu affichés datent du dernier chargement réussi.' vars={{ failure: p.failure }} parts={[(x) => <strong>{x}</strong>]} /></span>
          <button type="button" onClick={p.onRetry} style={{
            height: 32, padding: '0 13px', border: 'none', borderRadius: 'var(--r-xs)',
            background: 'var(--rose-fg)', color: '#fff', fontSize: 'var(--t-body)', fontWeight: 700, whiteSpace: 'nowrap', cursor: 'pointer',
          }}>{t('Réessayer')}</button>
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, alignItems: 'center', marginBottom: 12 }}>
        {p.exclusions.map((x, i) => (
          <span key={`${x.dim}:${x.value}`} style={{
            display: 'inline-flex', alignItems: 'center', gap: 8, height: 28, padding: '0 4px 0 10px',
            borderRadius: 'var(--r-pill)', background: AMBRE_BG, border: '1px solid var(--amber)',
          }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-micro)', letterSpacing: '.06em', textTransform: 'uppercase', color: AMBRE_FG }}>
              {sxDimLabel(x.dim)}
            </span>
            <span style={{ fontSize: 'var(--t-body)', fontWeight: 600, color: 'var(--ink-900)' }}>{displayLabels[`${x.dim}:${x.value}`] ?? x.value}</span>
            <button type="button" onClick={() => retirer(i)} aria-label={t('Retirer l’exclusion {v} {value}', { v: sxDimLabel(x.dim), value: x.value })}
              style={{
                width: 20, height: 20, border: 'none', borderRadius: 'var(--r-pill)', background: 'transparent',
                color: AMBRE_FG, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0, cursor: 'pointer',
              }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.6} strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
            </button>
          </span>
        ))}
        {p.exclusions.length === 0 && (
          <span style={{ fontSize: 'var(--t-body)', color: 'var(--ink-400)' }}>{t('Aucune exclusion — toute la boutique éligible sera démarquée.')}</span>
        )}

        <span style={{ position: 'relative', display: 'inline-flex' }}>
          <button type="button" onClick={() => setOuvert(!ouvert)} style={{
            height: 28, padding: '0 11px', border: '1px dashed var(--ink-300)', borderRadius: 'var(--r-pill)',
            background: 'var(--paper)', color: 'var(--ink-600)', fontSize: 'var(--t-dense)', fontWeight: 600,
            display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', cursor: 'pointer',
          }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
            
            {t('Ajouter une exclusion')}
          </button>
          {ouvert && (
            <PanneauChoix
              exclusions={p.exclusions}
              onToggle={(dim, value, label) => {
                const dedans = p.exclusions.some((x) => x.dim === dim && x.value === value);
                if (dedans) p.onChange(p.exclusions.filter((x) => !(x.dim === dim && x.value === value)));
                else {
                  if (dim === 'code' && label) setLibelles((l) => ({ ...l, [`${dim}:${value}`]: label }));
                  p.onChange([...p.exclusions, { dim, value }]);
                }
              }}
              onClose={() => setOuvert(false)}
            />
          )}
        </span>

        {p.exclusions.length >= 8 && (
          <button type="button" onClick={() => { void toutRetirer(); }} style={{
            height: 28, padding: '0 10px', border: 'none', borderRadius: 'var(--r-pill)', background: 'transparent',
            color: 'var(--ink-500)', fontSize: 'var(--t-dense)', fontWeight: 600, whiteSpace: 'nowrap', cursor: 'pointer',
          }}>{t('Tout retirer')}</button>
        )}
      </div>

      {p.dirty && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, padding: '12px 15px', borderRadius: 'var(--r-sm)',
          background: AMBRE_BG, border: '1px solid var(--amber)', marginBottom: 12, flexWrap: 'wrap',
        }}>
          <span style={{ flex: 1, minWidth: 210, fontSize: 'var(--t-body)', color: 'var(--ink-800)', lineHeight: 1.5 }}>
            <Trans k='<1>Campagne active</1> — les caisses ne connaissent pas encore ces changements. La réécriture applique les ajouts et retire les exclusions décochées.' parts={[(x) => <strong>{x}</strong>]} /></span>
          <button type="button" disabled={p.ecriture} onClick={p.onUpdate} style={{
            height: 36, padding: '0 14px', border: 'none', borderRadius: 'var(--r-ctrl)', background: 'var(--teal-500)',
            color: '#fff', fontSize: 'var(--t-body)', fontWeight: 700, whiteSpace: 'nowrap', cursor: 'pointer', opacity: p.ecriture ? 0.6 : 1,
          }}>{t('Mettre à jour les démarques')}</button>
        </div>
      )}

      {/* ⚠️ La règle contre-intuitive, DITE : ce n'est pas une photo prise à l'activation. */}
      <div style={{
        padding: '13px 15px', borderRadius: 'var(--r-sm)', background: 'var(--ink-50)',
        border: '1px solid var(--ink-200)', fontSize: 'var(--t-body)', color: 'var(--ink-600)', lineHeight: 1.6,
      }}>
        <Trans k='Un article qui <1>arrive en stock pendant les soldes</1> est démarqué comme les autres — sauf si sa saison figure dans les exclusions. Ce n’est pas une photo prise à l’activation.' parts={[(x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>]} /></div>
    </div>
  );
}

/**
 * Le panneau ancré : la dimension, la recherche, les valeurs à cocher.
 * ⚠️ Recherche DÉBOUNCÉE ET VERSIONNÉE — la réponse d'une frappe dépassée ne doit jamais recouvrir
 * celle de la frappe courante (le même piège que la palette et la recherche d'articles).
 */
function PanneauChoix({ exclusions, onToggle, onClose }: {
  exclusions: ExclusionSoldes[];
  onToggle: (dim: ExclusionSoldes['dim'], value: string, label?: string) => void;
  onClose: () => void;
}) {
  const [dim, setDim] = useState<ExclusionSoldes['dim']>('sais');
  // ⚠️ ÉCHAP FERME LE PANNEAU, ET LE FOCUS Y ENTRE PUIS REVIENT AU BOUTON : au seul pointeur, il ne se quittait pas au clavier.
  const { panelRef } = useOverlayPanel({ open: true, onClose });
  const [q, setQ] = useState('');
  const [options, setOptions] = useState<OptionSx[]>([]);
  const [failure, setFailure] = useState<string | null>(null);
  const [charge, setCharge] = useState(false);
  const champRef = useRef<HTMLInputElement>(null);

  useEffect(() => { champRef.current?.focus(); }, []);

  const terme = q.trim();
  // L'article se cherche par NOM ou CODE : sous 2 caractères, une recherche large n'aide personne.
  const besoinTerme = dim === 'code' && terme.length < 2;

  useEffect(() => {
    if (besoinTerme) { setOptions([]); setFailure(null); setCharge(false); return; }
    let vivant = true;
    setCharge(true);
    const timer = window.setTimeout(() => {
      const p = dim === 'code'
        ? api.articles({ page: 1, size: 12, sort: 'designation', dir: 'asc', q: terme })
          .then((r) => (r.rows ?? []).map((a): OptionSx => ({
            value: a.modelKey, label: a.name,
            sub: [a.articleCode ?? a.gencode, a.brand].filter(Boolean).join(' · '),
          })))
        : api.articleFacetSearch(dim as ArticleFacet, terme)
          .then((r) => r.map((f): OptionSx => ({
            value: f.value, label: f.value,
            sub: t('{n} modèle', { n: f.count }),
          })));
      p.then((rows) => { if (vivant) { setOptions(rows); setFailure(null); } })
        .catch((e: unknown) => { if (vivant) { setOptions([]); setFailure(e instanceof Error ? e.message : String(e)); } })
        .finally(() => { if (vivant) setCharge(false); });
    }, 250);
    return () => { vivant = false; window.clearTimeout(timer); };
  }, [dim, terme, besoinTerme]);

  const coche = useMemo(
    () => new Set(exclusions.map((x) => `${x.dim}:${x.value}`)),
    [exclusions],
  );

  return (
    <>
      {/* Le voile qui ferme au clic extérieur — sous le panneau, au-dessus du reste. */}
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 1099 }} />
      <div ref={panelRef} role="dialog" aria-label={t('Ajouter une exclusion')} style={{
        position: 'absolute', top: 34, left: 0, zIndex: 1100, width: 372, maxWidth: '88vw',
        background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)',
        boxShadow: '0 18px 44px -12px rgba(12,14,16,.28)', overflow: 'hidden',
      }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', padding: 7, gap: 3, background: 'var(--ink-100)', borderBottom: '1px solid var(--ink-200)' }}>
          {SX_DIMS.map((k) => {
            const on = dim === k;
            return (
              <button key={k} type="button" onClick={() => { setDim(k); setQ(''); }} style={{
                height: 26, padding: '0 9px', border: 'none', borderRadius: 'var(--r-xs)',
                background: on ? 'var(--paper)' : 'transparent',
                color: on ? 'var(--ink-900)' : 'var(--ink-500)',
                boxShadow: on ? '0 1px 4px rgba(12,14,16,.14)' : 'none',
                fontSize: 'var(--t-note)', fontWeight: 600, whiteSpace: 'nowrap', cursor: 'pointer',
              }}>{sxDimLabel(k)}</button>
            );
          })}
        </div>

        <div style={{ padding: 8, borderBottom: '1px solid var(--ink-100)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: 32, padding: '0 10px', borderRadius: 'var(--r-xs)', background: 'var(--ink-50)', color: 'var(--ink-400)' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" />
            </svg>
            <input ref={champRef} value={q} onChange={(e) => setQ(e.target.value)}
              placeholder={dim === 'code' ? t('Nom ou code article — 2 caractères minimum') : t('Rechercher une valeur…')}
              style={{ flex: 1, minWidth: 0, border: 'none', background: 'transparent', fontSize: 'var(--t-body)', color: 'var(--ink-900)', outline: 'none' }} />
          </div>
        </div>

        <div style={{ maxHeight: 238, overflowY: 'auto', padding: 5 }}>
          {options.map((o) => {
            const on = coche.has(`${dim}:${o.value}`);
            return (
              <button key={o.value} type="button" onClick={() => onToggle(dim, o.value, o.label)} style={{
                display: 'flex', alignItems: 'center', gap: 9, width: '100%', padding: '7px 10px',
                border: 'none', borderRadius: 'var(--r-xs)', background: 'transparent', textAlign: 'left', cursor: 'pointer',
              }}>
                <span style={{
                  width: 16, height: 16, borderRadius: 'var(--r-xs)', boxSizing: 'border-box',
                  border: `1.5px solid ${on ? 'var(--teal-500)' : 'var(--ink-300)'}`,
                  background: on ? 'var(--teal-500)' : 'var(--paper)',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  {on && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={3.4} strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>}
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 'var(--t-body)', fontWeight: 600, color: 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{o.label}</span>
                  <span style={{ display: 'block', fontSize: 'var(--t-sub)', color: 'var(--ink-400)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{o.sub}</span>
                </span>
              </button>
            );
          })}
          {besoinTerme && <div style={{ padding: '16px 10px', textAlign: 'center', fontSize: 'var(--t-dense)', color: 'var(--ink-400)' }}>{t('Tapez au moins 2 caractères — nom ou code article.')}</div>}
          {/* ⚠️ « en cours » et « rien trouvé » sont DEUX états : les confondre ferait lire « cette
              valeur n'existe pas » pendant que la requête vole encore. */}
          {!besoinTerme && charge && <div style={{ padding: '16px 10px', textAlign: 'center', fontSize: 'var(--t-dense)', color: 'var(--ink-400)' }}>{t('Recherche…')}</div>}
          {!besoinTerme && !charge && failure !== null && (
            <div role="alert" style={{ padding: '14px 10px', textAlign: 'center', fontSize: 'var(--t-dense)', color: 'var(--rose-fg)' }}>
              {t('La recherche a échoué : {failure}', { failure })}
            </div>
          )}
          {!besoinTerme && !charge && failure === null && options.length === 0 && (
            <div style={{ padding: '16px 10px', textAlign: 'center', fontSize: 'var(--t-dense)', color: 'var(--ink-400)' }}>
              {terme === '' ? t('Tapez pour chercher une valeur.') : t('Aucune valeur ne correspond.')}
            </div>
          )}
          {options.length >= 12 && (
            <div style={{ padding: '7px 10px', fontSize: 'var(--t-meta)', color: 'var(--ink-400)', background: 'var(--ink-50)', borderRadius: 'var(--r-xs)' }}>
              
              {t('Affinez la recherche — 12 premières valeurs affichées.')}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: 8, borderTop: '1px solid var(--ink-200)', background: 'var(--ink-50)' }}>
          <span style={{ flex: 1, fontSize: 'var(--t-meta)', color: 'var(--ink-500)', paddingLeft: 4 }}>{t('Cochez, décochez — le compte se recalcule à chaque geste.')}</span>
          <button type="button" onClick={onClose} style={{
            height: 30, padding: '0 12px', border: 'none', borderRadius: 'var(--r-xs)',
            background: 'var(--ink-900)', color: 'var(--paper)', fontSize: 'var(--t-dense)', fontWeight: 700, cursor: 'pointer',
          }}>{t('Fermer')}</button>
        </div>
      </div>
    </>
  );
}
